import { memo, useMemo } from 'react';
import { Sparkles, TrendingUp, AlertTriangle, CheckCircle, Target, Lightbulb } from 'lucide-react';
import type { Lead, Deal } from '@/types';
import type {
  HistoricalFunnelData,
  StageVelocity,
  LeadAgingData,
  LeadToDealConversion,
  WinLossData,
} from '@/utils/analytics';

interface AIAnalysisProps {
  leads: Lead[];
  deals: Deal[];
  weightedPipeline: number;
  leadVelocity: { lvr: number; thisMonth: number; lastMonth: number };
  winLossData: WinLossData;
  funnel: HistoricalFunnelData | null;
  stageVelocity: StageVelocity[];
  leadAging: LeadAgingData[];
  leadToDealConversion: LeadToDealConversion;
  slippedDeals: Deal[];
  isLoading?: boolean;
}

interface Insight {
  type: 'positive' | 'warning' | 'negative' | 'info';
  category: string;
  title: string;
  description: string;
  metric?: string;
  priority: number; // 1-5, lower is higher priority
}

/**
 * Generate AI-powered insights from marketing data
 */
function generateInsights(props: AIAnalysisProps): Insight[] {
  const {
    leads: _leads, // Reserved for future lead-specific insights
    deals,
    weightedPipeline,
    leadVelocity,
    winLossData,
    funnel,
    stageVelocity,
    leadAging,
    leadToDealConversion,
    slippedDeals,
  } = props;

  const insights: Insight[] = [];

  // ========== PIPELINE HEALTH ==========

  // Lead Velocity Rate analysis
  if (leadVelocity.lvr > 20) {
    insights.push({
      type: 'positive',
      category: 'Pipeline Growth',
      title: 'Strong lead velocity',
      description: `Your qualified leads grew ${leadVelocity.lvr}% month-over-month. This is an excellent leading indicator for future revenue growth.`,
      metric: `+${leadVelocity.lvr}% LVR`,
      priority: 1,
    });
  } else if (leadVelocity.lvr > 0) {
    insights.push({
      type: 'positive',
      category: 'Pipeline Growth',
      title: 'Positive lead growth',
      description: `Lead velocity is positive at ${leadVelocity.lvr}%. Maintain marketing activities to sustain this growth trajectory.`,
      metric: `+${leadVelocity.lvr}% LVR`,
      priority: 2,
    });
  } else if (leadVelocity.lvr < -20) {
    insights.push({
      type: 'negative',
      category: 'Pipeline Growth',
      title: 'Significant lead decline',
      description: `Qualified leads dropped ${Math.abs(leadVelocity.lvr)}% from last month (${leadVelocity.lastMonth} → ${leadVelocity.thisMonth}). Review marketing channels and lead generation strategies urgently.`,
      metric: `${leadVelocity.lvr}% LVR`,
      priority: 1,
    });
  } else if (leadVelocity.lvr < 0) {
    insights.push({
      type: 'warning',
      category: 'Pipeline Growth',
      title: 'Lead volume declining',
      description: `Lead velocity is negative at ${leadVelocity.lvr}%. Consider increasing marketing efforts to reverse this trend.`,
      metric: `${leadVelocity.lvr}% LVR`,
      priority: 2,
    });
  }

  // Win Rate analysis
  if (winLossData.winRate >= 40) {
    insights.push({
      type: 'positive',
      category: 'Sales Effectiveness',
      title: 'Excellent win rate',
      description: `${winLossData.winRate}% win rate is above industry average (typically 20-30%). Your sales qualification and closing processes are working well.`,
      metric: `${winLossData.winRate}% win rate`,
      priority: 2,
    });
  } else if (winLossData.winRate >= 25) {
    insights.push({
      type: 'info',
      category: 'Sales Effectiveness',
      title: 'Healthy win rate',
      description: `${winLossData.winRate}% win rate is within industry norms. Look for opportunities in deal qualification to improve further.`,
      metric: `${winLossData.winRate}% win rate`,
      priority: 3,
    });
  } else if (winLossData.winRate > 0 && winLossData.winRate < 20) {
    insights.push({
      type: 'warning',
      category: 'Sales Effectiveness',
      title: 'Win rate needs attention',
      description: `${winLossData.winRate}% win rate is below typical benchmarks. Consider reviewing deal qualification criteria or sales training.`,
      metric: `${winLossData.winRate}% win rate`,
      priority: 2,
    });
  }

  // ========== SLIPPED DEALS ==========

  if (slippedDeals.length > 0) {
    const totalSlippedValue = slippedDeals.reduce((sum, d) => sum + d.value, 0);
    const avgDaysOverdue = Math.round(
      slippedDeals.reduce((sum, d) => {
        const closeDate = new Date(d.closeDate!);
        const now = new Date();
        return sum + Math.floor((now.getTime() - closeDate.getTime()) / (1000 * 60 * 60 * 24));
      }, 0) / slippedDeals.length
    );

    if (slippedDeals.length >= 5 || totalSlippedValue > weightedPipeline * 0.3) {
      insights.push({
        type: 'negative',
        category: 'Forecast Risk',
        title: 'Critical: Multiple slipped deals',
        description: `${slippedDeals.length} deals worth $${(totalSlippedValue / 1000).toFixed(0)}K have passed their close dates (avg ${avgDaysOverdue} days overdue). Update forecasts and re-engage these opportunities immediately.`,
        metric: `${slippedDeals.length} deals overdue`,
        priority: 1,
      });
    } else {
      insights.push({
        type: 'warning',
        category: 'Forecast Risk',
        title: 'Deals past close date',
        description: `${slippedDeals.length} deal${slippedDeals.length > 1 ? 's' : ''} worth $${(totalSlippedValue / 1000).toFixed(0)}K ${slippedDeals.length > 1 ? 'have' : 'has'} slipped past expected close date. Review and update close dates or re-engage.`,
        metric: `${slippedDeals.length} slipped`,
        priority: 2,
      });
    }
  }

  // ========== CONVERSION FUNNEL ==========

  if (funnel && funnel.stages.length > 0) {
    // Find biggest drop-off
    let biggestDropoff = { stage: '', rate: 0, fromStage: '' };
    for (let i = 1; i < funnel.stages.length; i++) {
      const prevStage = funnel.stages[i - 1];
      const currStage = funnel.stages[i];
      if (prevStage && currStage && prevStage.passedThrough > 0) {
        const dropoff = 100 - (currStage.passedThrough / prevStage.passedThrough) * 100;
        if (dropoff > biggestDropoff.rate && dropoff > 40) {
          biggestDropoff = { stage: currStage.stage, rate: Math.round(dropoff), fromStage: prevStage.stage };
        }
      }
    }

    if (biggestDropoff.rate > 60) {
      insights.push({
        type: 'negative',
        category: 'Conversion Funnel',
        title: 'Major funnel bottleneck',
        description: `${biggestDropoff.rate}% drop-off between "${biggestDropoff.fromStage}" and "${biggestDropoff.stage}". This is your biggest conversion opportunity - investigate why leads are stalling here.`,
        metric: `${biggestDropoff.rate}% drop-off`,
        priority: 1,
      });
    } else if (biggestDropoff.rate > 40) {
      insights.push({
        type: 'warning',
        category: 'Conversion Funnel',
        title: 'Funnel friction detected',
        description: `${biggestDropoff.rate}% of leads drop between "${biggestDropoff.fromStage}" and "${biggestDropoff.stage}". Review your nurturing process at this stage.`,
        metric: `${biggestDropoff.rate}% drop-off`,
        priority: 2,
      });
    }

    // Overall funnel efficiency
    if (funnel.overallConversionRate >= 15) {
      insights.push({
        type: 'positive',
        category: 'Conversion Funnel',
        title: 'Strong funnel efficiency',
        description: `${funnel.overallConversionRate}% of leads progress through your entire funnel. This indicates good lead quality and effective nurturing.`,
        metric: `${funnel.overallConversionRate}% conversion`,
        priority: 3,
      });
    } else if (funnel.overallConversionRate < 5 && funnel.totalLeadsInPeriod > 10) {
      insights.push({
        type: 'warning',
        category: 'Conversion Funnel',
        title: 'Low funnel throughput',
        description: `Only ${funnel.overallConversionRate}% of leads complete the funnel. Consider reviewing lead quality or nurturing touchpoints.`,
        metric: `${funnel.overallConversionRate}% conversion`,
        priority: 3,
      });
    }
  }

  // ========== LEAD AGING ==========

  const stuckLeads = leadAging.reduce((sum, stage) => {
    const over30Days = stage.buckets.find(b => b.label === '> 30 days');
    return sum + (over30Days?.count || 0);
  }, 0);

  const totalAgingLeads = leadAging.reduce((sum, stage) => sum + stage.total, 0);

  if (stuckLeads > 0 && totalAgingLeads > 0) {
    const stuckPercentage = Math.round((stuckLeads / totalAgingLeads) * 100);

    if (stuckPercentage >= 30) {
      insights.push({
        type: 'negative',
        category: 'Lead Health',
        title: 'Many leads going stale',
        description: `${stuckPercentage}% of leads (${stuckLeads}) have been in their current stage for over 30 days. These need immediate attention to prevent them from going cold.`,
        metric: `${stuckLeads} stale leads`,
        priority: 1,
      });
    } else if (stuckPercentage >= 15) {
      insights.push({
        type: 'warning',
        category: 'Lead Health',
        title: 'Leads aging in pipeline',
        description: `${stuckLeads} leads (${stuckPercentage}%) have been stagnant for 30+ days. Consider re-engagement campaigns or qualification review.`,
        metric: `${stuckLeads} aging leads`,
        priority: 2,
      });
    }
  }

  // ========== STAGE VELOCITY ==========

  if (stageVelocity.length > 0) {
    // Find slowest stage
    const slowestStage = stageVelocity.reduce((prev, curr) =>
      curr.avgDays > prev.avgDays ? curr : prev
    );

    if (slowestStage.avgDays > 14 && slowestStage.count >= 3) {
      insights.push({
        type: 'warning',
        category: 'Process Efficiency',
        title: 'Stage bottleneck identified',
        description: `Leads spend an average of ${slowestStage.avgDays} days in "${slowestStage.stage}" stage. Look for ways to accelerate this stage of your process.`,
        metric: `${slowestStage.avgDays} days avg`,
        priority: 3,
      });
    }

    // Average velocity across all stages
    const avgVelocity = Math.round(
      stageVelocity.reduce((sum, s) => sum + s.avgDays * s.count, 0) /
      stageVelocity.reduce((sum, s) => sum + s.count, 0) || 0
    );

    if (avgVelocity <= 5) {
      insights.push({
        type: 'positive',
        category: 'Process Efficiency',
        title: 'Fast pipeline velocity',
        description: `Leads move through stages in ${avgVelocity} days on average. Your sales process is efficient.`,
        metric: `${avgVelocity} days avg`,
        priority: 4,
      });
    }
  }

  // ========== LEAD-TO-DEAL CONVERSION ==========

  if (leadToDealConversion.totalLeads >= 10) {
    if (leadToDealConversion.conversionRate >= 20) {
      insights.push({
        type: 'positive',
        category: 'Lead Quality',
        title: 'Strong lead-to-deal conversion',
        description: `${leadToDealConversion.conversionRate}% of leads convert to deals. Your lead qualification and nurturing are effective.`,
        metric: `${leadToDealConversion.conversionRate}% converted`,
        priority: 2,
      });
    } else if (leadToDealConversion.conversionRate < 10 && leadToDealConversion.totalLeads >= 20) {
      insights.push({
        type: 'warning',
        category: 'Lead Quality',
        title: 'Low lead conversion',
        description: `Only ${leadToDealConversion.conversionRate}% of leads become deals. Review lead sources and qualification criteria.`,
        metric: `${leadToDealConversion.conversionRate}% converted`,
        priority: 2,
      });
    }

    // Average days to convert
    if (leadToDealConversion.avgDaysToConvert <= 14 && leadToDealConversion.convertedCount >= 5) {
      insights.push({
        type: 'positive',
        category: 'Sales Cycle',
        title: 'Fast conversion cycle',
        description: `Leads convert to deals in ${leadToDealConversion.avgDaysToConvert} days on average. This quick turnaround indicates strong product-market fit.`,
        metric: `${leadToDealConversion.avgDaysToConvert} days`,
        priority: 3,
      });
    } else if (leadToDealConversion.avgDaysToConvert > 30 && leadToDealConversion.convertedCount >= 5) {
      insights.push({
        type: 'info',
        category: 'Sales Cycle',
        title: 'Extended conversion cycle',
        description: `Average lead-to-deal time is ${leadToDealConversion.avgDaysToConvert} days. Consider ways to accelerate early-stage qualification.`,
        metric: `${leadToDealConversion.avgDaysToConvert} days`,
        priority: 4,
      });
    }
  }

  // ========== PIPELINE VALUE ==========

  if (weightedPipeline > 0) {
    const avgDealValue = deals.length > 0
      ? deals.reduce((sum, d) => sum + d.value, 0) / deals.length
      : 0;

    if (avgDealValue > 50000) {
      insights.push({
        type: 'info',
        category: 'Deal Size',
        title: 'Enterprise deal profile',
        description: `Average deal size is $${(avgDealValue / 1000).toFixed(0)}K, indicating enterprise-level opportunities. Ensure sales process matches buyer complexity.`,
        metric: `$${(avgDealValue / 1000).toFixed(0)}K avg`,
        priority: 4,
      });
    }
  }

  // Sort by priority (lower number = higher priority)
  return insights.sort((a, b) => a.priority - b.priority);
}

function InsightCard({ insight }: { insight: Insight }) {
  const icons = {
    positive: CheckCircle,
    warning: AlertTriangle,
    negative: AlertTriangle,
    info: Lightbulb,
  };

  const colors = {
    positive: {
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      icon: 'text-emerald-400',
      badge: 'bg-emerald-500/20 text-emerald-400',
    },
    warning: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/20',
      icon: 'text-yellow-400',
      badge: 'bg-yellow-500/20 text-yellow-400',
    },
    negative: {
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      icon: 'text-red-400',
      badge: 'bg-red-500/20 text-red-400',
    },
    info: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/20',
      icon: 'text-blue-400',
      badge: 'bg-blue-500/20 text-blue-400',
    },
  };

  const Icon = icons[insight.type];
  const colorScheme = colors[insight.type];

  return (
    <div className={`rounded-lg border ${colorScheme.border} ${colorScheme.bg} p-4`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${colorScheme.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${colorScheme.badge}`}>
              {insight.category}
            </span>
            {insight.metric && (
              <span className="text-xs text-zinc-400 font-mono">{insight.metric}</span>
            )}
          </div>
          <h4 className="text-sm font-medium text-white mb-1">{insight.title}</h4>
          <p className="text-xs text-zinc-400 leading-relaxed">{insight.description}</p>
        </div>
      </div>
    </div>
  );
}

export const AIAnalysis = memo(function AIAnalysis(props: AIAnalysisProps) {
  const { isLoading } = props;

  const insights = useMemo(() => generateInsights(props), [props]);

  // Separate insights by type for summary
  const positiveCount = insights.filter(i => i.type === 'positive').length;
  const warningCount = insights.filter(i => i.type === 'warning').length;
  const negativeCount = insights.filter(i => i.type === 'negative').length;

  // Get top recommendations (first 2-3 actionable items)
  const topRecommendations = insights
    .filter(i => i.type === 'warning' || i.type === 'negative')
    .slice(0, 3);

  const strengths = insights.filter(i => i.type === 'positive').slice(0, 3);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30">
          <Sparkles className="h-5 w-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">AI Analysis</h3>
          <p className="text-xs text-zinc-500">Automated insights from your marketing data</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-purple-400" />
            <span className="text-sm text-zinc-400">Analyzing data...</span>
          </div>
        </div>
      ) : insights.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          <p>Not enough data to generate insights.</p>
          <p className="text-xs mt-1">Add more leads and deals to see AI analysis.</p>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                <span className="text-lg font-bold text-emerald-400">{positiveCount}</span>
              </div>
              <p className="text-[10px] text-zinc-400">Strengths</p>
            </div>
            <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <AlertTriangle className="h-4 w-4 text-yellow-400" />
                <span className="text-lg font-bold text-yellow-400">{warningCount}</span>
              </div>
              <p className="text-[10px] text-zinc-400">Watch Items</p>
            </div>
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Target className="h-4 w-4 text-red-400" />
                <span className="text-lg font-bold text-red-400">{negativeCount}</span>
              </div>
              <p className="text-[10px] text-zinc-400">Action Needed</p>
            </div>
          </div>

          {/* Top Recommendations */}
          {topRecommendations.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-red-400" />
                Priority Actions
              </h4>
              <div className="space-y-3">
                {topRecommendations.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </div>
          )}

          {/* Strengths */}
          {strengths.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                What's Working Well
              </h4>
              <div className="space-y-3">
                {strengths.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </div>
          )}

          {/* Other Insights */}
          {insights.filter(i => i.type === 'info').length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-blue-400" />
                Additional Insights
              </h4>
              <div className="space-y-3">
                {insights.filter(i => i.type === 'info').slice(0, 3).map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});
